import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule, Location, JsonPipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subscription, catchError, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { ApiService, DebugDoc, DebugEvent } from '../../services/api.service';

@Component({
  selector: 'app-debug',
  standalone: true,
  imports: [
    CommonModule,
    JsonPipe,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './debug.component.html',
  styleUrl: './debug.component.scss',
})
export class DebugComponent implements OnInit, OnDestroy {
  debugId: string | null = null;
  loading = false;
  overlayVisible = false;
  overlayFadingOut = false;
  private overlayTimeout: ReturnType<typeof setTimeout> | null = null;
  error: string | null = null;

  filter = '';

  doc: DebugDoc | null = null;
  events: DebugEvent[] = [];
  filteredEvents: DebugEvent[] = [];

  private api = inject(ApiService);
  private subs = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private location: Location,
  ) {}

  ngOnInit() {
    this.subs.add(
      this.route.paramMap
        .pipe(
          map((params) => params.get('debugId')),
          distinctUntilChanged(),
          switchMap((debugId) => {
            this.debugId = debugId;
            this.error = null;
            // Keep previous doc visible until new one arrives; we’ll cover it with a white veil.

            if (!this.debugId) {
              this.error = 'Missing debug id.';
              return of<DebugDoc | null>(null);
            }

            this.setLoading(true);
            return this.api.getDebugCached(this.debugId).pipe(
              catchError((err) => {
                console.error('Error loading debug doc:', err);
                this.error = 'Failed to load debug data.';
                return of(null);
              }),
            );
          }),
        )
        .subscribe((doc) => {
          this.setLoading(false);
          if (!doc) return;

          this.doc = doc;
          const events = (doc.events ?? []).slice();
          events.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
          this.events = events;
          this.applyFilter();
        }),
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onFilterChange(value: string) {
    this.filter = value;
    this.applyFilter();
  }

  back() {
    this.location.back();
  }

  trackByIndex(index: number) {
    return index;
  }

  isUserQueryEvent(event: DebugEvent): boolean {
    return event.node === 'user_query';
  }

  private applyFilter() {
    const q = this.filter.trim().toLowerCase();
    this.filteredEvents = q ? this.events.filter((e) => e.node.toLowerCase().includes(q)) : this.events;
  }

  private setLoading(loading: boolean) {
    this.loading = loading;

    if (this.overlayTimeout) {
      clearTimeout(this.overlayTimeout);
      this.overlayTimeout = null;
    }

    if (loading) {
      // Always show immediately to reduce flicker.
      this.overlayVisible = true;
      this.overlayFadingOut = false;
      return;
    }

    // Fade out quickly, then remove from DOM.
    this.overlayFadingOut = true;
    this.overlayTimeout = setTimeout(() => {
      this.overlayVisible = false;
      this.overlayFadingOut = false;
    }, 600);
  }
}

